
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2 } from 'lucide-react';
import { MockupItem } from '../types';
import { Language } from '../App';

const Carousel: React.FC<{ lang: Language }> = ({ lang }) => {
  const [selectedItem, setSelectedItem] = useState<MockupItem | null>(null);

  const mockups: MockupItem[] = [
    { 
      id: 1, 
      title: lang === 'pt' ? 'Login Seguro' : 'Secure Login', 
      description: lang === 'pt' ? 'Interface limpa e intuitiva para retenção máxima.' : 'Clean and intuitive interface for maximum retention.', 
      image: 'https://i.imgur.com/QKieW99.jpeg', 
      benefit: lang === 'pt' ? 'Onboarding veloz que reduz o churn de novos usuários.' : 'Fast onboarding that reduces new user churn.' 
    },
    { 
      id: 2, 
      title: lang === 'pt' ? 'Compra via PIX' : 'Instant Buy', 
      description: lang === 'pt' ? 'Liquidez instantânea do mundo real para o seu token.' : 'Instant real-world liquidity for your token.', 
      image: 'https://i.imgur.com/52ListL.jpeg', 
      benefit: lang === 'pt' ? 'Fim das barreiras técnicas de entrada para investidores brasileiros.' : 'End of technical entry barriers for Brazilian investors.' 
    },
    { 
      id: 3, 
      title: lang === 'pt' ? 'Smart Swap' : 'Smart Swap', 
      description: lang === 'pt' ? 'Trocas descentralizadas com a melhor cotação.' : 'Decentralized exchanges with the best rates.', 
      image: 'https://i.imgur.com/U4ibzK3.jpeg', 
      benefit: lang === 'pt' ? 'Experiência DeFi completa sem sair da custódia do usuário.' : 'Full DeFi experience without leaving user custody.' 
    },
    { 
      id: 4, 
      title: lang === 'pt' ? 'Token Listado' : 'Listed Token', 
      description: lang === 'pt' ? 'Visibilidade premium no portfólio dos usuários.' : 'Premium visibility in user portfolios.', 
      image: 'https://i.imgur.com/TwLezFg.jpeg', 
      benefit: lang === 'pt' ? 'Status institucional e reconhecimento visual imediato.' : 'Institutional status and immediate visual recognition.' 
    },
    { 
      id: 5, 
      title: lang === 'pt' ? 'Banner Promocional' : 'Promotional Banner', 
      description: lang === 'pt' ? 'Marketing direto no ponto de venda.' : 'Direct marketing at the point of sale.', 
      image: 'https://i.imgur.com/GPhBU0N.jpeg', 
      benefit: lang === 'pt' ? 'Aumente o volume de trading com exposição segmentada.' : 'Increase trading volume with targeted exposure.' 
    },
    { 
      id: 6, 
      title: lang === 'pt' ? 'Ranking / Upvote' : 'Ranking / Upvote', 
      description: lang === 'pt' ? 'Gamificação que gera prova social.' : 'Gamification that generates social proof.', 
      image: 'https://i.imgur.com/Xh8F83s.jpeg', 
      benefit: lang === 'pt' ? 'Engaje sua comunidade para elevar seu projeto no ranking.' : 'Engage your community to boost your project in the ranking.' 
    },
    { 
      id: 7, 
      title: lang === 'pt' ? 'Jhow AI' : 'Jhow AI', 
      description: lang === 'pt' ? 'Seu projeto integrado ao Jhow, assistente de IA da Defi Bank.' : 'Your project integrated with Jhow, DeFi Bank AI assistant.', 
      image: 'https://i.imgur.com/Qvxd1DU.jpeg', 
      benefit: lang === 'pt' ? 'Conecte seu projeto ao estilo de vida dos usuários.' : 'Connect your project to the users lifestyle.' 
    },
  ];

  return (
    <section className="py-20 md:py-40 bg-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 md:mb-24 text-center relative z-20">
        <h2 className="text-3xl md:text-7xl font-black mb-4 md:mb-6 tracking-tighter uppercase italic leading-none">
          {lang === 'pt' ? 'A ' : 'The '} <span className="gradient-text">{lang === 'pt' ? 'Jornada' : 'User Journey'}</span> {lang === 'pt' ? 'do Usuário' : ''}
        </h2>
        <p className="text-zinc-500 max-w-2xl mx-auto font-medium text-sm md:text-lg leading-relaxed px-4">
          {lang === 'pt' 
            ? 'Navegue pelo ecossistema através de uma interface desenhada para converter curiosidade em transação.'
            : 'Navigate through the ecosystem via an interface designed to convert curiosity into transaction.'}
        </p>
      </div>

      <div className="relative flex justify-center items-end min-h-[450px] md:min-h-[800px] max-w-7xl mx-auto px-4">
        <div className="relative flex items-end justify-center w-full h-full pb-10 md:pb-20">
          {mockups.map((item, index) => {
            const total = mockups.length;
            const mid = Math.floor(total / 2);
            const offset = index - mid;
            const rotation = offset * 6; 
            const xTranslation = offset * (window.innerWidth < 768 ? 35 : 55); 
            
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 50, rotate: rotation }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.6 }}
                whileHover={{ 
                  y: window.innerWidth < 768 ? -50 : -150, 
                  rotate: 0, 
                  scale: 1.1, 
                  zIndex: 100,
                  transition: { duration: 0.3 }
                }}
                onClick={() => setSelectedItem(item)}
                className="absolute cursor-pointer w-[130px] md:w-[260px] origin-bottom group"
                style={{ 
                  x: `${xTranslation}px`,
                  zIndex: 10 + index
                }}
              >
                <div className="bg-zinc-900 border border-zinc-800 rounded-[1.5rem] md:rounded-[2.8rem] overflow-hidden shadow-2xl relative ring-1 ring-white/5 group-hover:ring-orange-500 transition-all duration-300">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="gradient-bg text-white text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg whitespace-nowrap">
                      <Maximize2 size={10} /> {lang === 'pt' ? 'Ver Detalhes' : 'View Details'}
                    </div>
                  </div>

                  <div className="aspect-[9/19.5] relative">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-500" 
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                       <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                          <Maximize2 className="text-white" size={20} />
                       </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/98 backdrop-blur-2xl"
            onClick={() => setSelectedItem(null)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-950 rounded-[2rem] md:rounded-[3.5rem] border border-white/10 max-w-5xl w-full overflow-hidden flex flex-col md:flex-row relative shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <button 
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 md:top-8 md:right-8 z-30 bg-white/10 p-2 md:p-3 rounded-full text-white hover:gradient-bg transition-colors"
              >
                <X size={20} />
              </button>
              
              <div className="md:w-[40%] p-4 bg-black flex justify-center items-center">
                <img src={selectedItem.image} alt={selectedItem.title} className="max-h-[50vh] md:max-h-[70vh] object-contain rounded-[1.5rem]" />
              </div>
              
              <div className="md:w-[60%] p-6 md:p-16 flex flex-col justify-center text-left">
                <span className="text-orange-500 font-black text-[10px] uppercase tracking-[0.3em] mb-2 block">USER EXPERIENCE</span>
                <h3 className="text-3xl md:text-6xl font-black text-white mb-4 uppercase tracking-tighter leading-none italic">{selectedItem.title}</h3>
                <p className="text-zinc-400 text-base md:text-xl mb-8 leading-relaxed font-medium">{selectedItem.description}</p>
                <div className="bg-white/5 border border-white/10 p-6 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem]">
                  <h4 className="gradient-text font-black mb-2 uppercase tracking-[0.2em] text-[10px]">{lang === 'pt' ? 'Vantagem para o seu Projeto' : 'Project Benefit'}</h4>
                  <p className="text-white text-lg md:text-2xl font-bold italic leading-tight">"{selectedItem.benefit}"</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Carousel;
